import { useCallback, useMemo } from 'react';
import { useLocation } from '@reach/router';
import { useQueryFilter } from 'hooks/useQueryFilter';
import { useApikey } from 'hooks/useApikey';
import { useUser } from 'services/User';
import {
    CVEsInterface,
    FileResponseInterface,
    SandboxResponseInterface,
    VulnerabilityDataInterface,
    VulnerabilityResponseInterface,
} from 'services/interface';
import {
    axios,
    type Fetcher,
    type MutationFetcher,
    type SWRKey,
    useSWR,
    useSWRImmutable,
    useSWRInfinite,
    useSWRMutation,
} from 'services/swr';
import { getFileProgress } from 'utils/Format';
import { sandboxRefreshInterval } from 'utils/SandboxFunctions';

export const resolveResultFileSha1 = (fileData?: FileResponseInterface | null): string | undefined => {
    const nested = typeof fileData?.file_info?.sha1 === 'string' ? fileData.file_info.sha1.trim() : '';

    if (nested.length > 0) {
        return nested;
    }

    const root = typeof fileData?.sha1 === 'string' ? fileData.sha1.trim() : '';

    return root.length > 0 ? root : undefined;
};

export const useFileData = ({ dataId }: { dataId?: string | null; } = {}) => {
    const { pathname } = useLocation();
    const segments = pathname.split('/');
    const type = segments[2];
    const id = segments[3];

    const { serviceId } = useQueryFilter();
    const { apikey } = useApikey({ serviceId });

    const resultsKey = useMemo<SWRKey>(() => {
        if (!apikey || dataId === null) {
            return null;
        }

        const headers = { apikey };

        if (typeof dataId === 'string') {
            return { url: `/v4/file/${dataId}`, headers };
        }

        if (id && type === 'file') {
            return { url: `/v4/file/${id}`, headers };
        }

        if (id && type === 'hash') {
            return { url: `/v4/hash/${id}`, headers };
        }

        return null;
    }, [id, type, dataId, apikey]);

    const fileRefreshInterval = (data?: FileResponseInterface) => {
        const fileProgress = getFileProgress(
            data?.process_info?.progress_percentage,
            data?.scan_results?.progress_percentage,
        );

        const scanInProgress = data?.scan_results?.scan_all_result_i === 255;

        if (type === 'hash') {
            return 0;
        }

        if (!scanInProgress && fileProgress === 100) {
            return 0;
        }

        return 3000;
    };

    return useSWR<FileResponseInterface>(resultsKey, {
        refreshInterval: fileRefreshInterval,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });
};

export const useSandboxData = () => {
    const { serviceId } = useQueryFilter();
    const { apikey } = useApikey({ serviceId });

    const { data: fileData } = useFileData();
    const { lastSandboxId } = useQueryFilter();
    const sandboxId = lastSandboxId || fileData?.sandbox_id;
    const sandboxResponseKey: SWRKey =
        sandboxId && apikey ? { url: `/v4/sandbox/${sandboxId}`, headers: { apikey } } : null;

    return useSWR<SandboxResponseInterface>(sandboxResponseKey, {
        refreshInterval: sandboxRefreshInterval,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });
};

export const useSandboxReport = () => {
    const { serviceId } = useQueryFilter();
    const { apikey } = useApikey({ serviceId });
    const { data: sandboxData } = useSandboxData();
    const sandboxDataKey: SWRKey =
        sandboxData?.store_at && apikey ? { url: sandboxData.store_at, headers: { apikey } } : null;

    return useSWRImmutable(sandboxDataKey);
};

export const VULNERABILITY_LIMIT = 10;

export interface HashVulnerabilityRow {
    cve: string;
    severity: string;
}

export const parseHashVulnerabilityResponse = (raw: unknown): HashVulnerabilityRow[] => {
    if (typeof raw !== 'object' || raw === null) {
        return [];
    }

    const record = raw as Record<string, unknown>;
    const cvesUnknown = record.cves;
    const vulnerabilityUnknown = record.vulnerability;

    if (!Array.isArray(cvesUnknown) || cvesUnknown.length === 0) {
        return [];
    }

    const severity =
        typeof vulnerabilityUnknown === 'object' &&
            vulnerabilityUnknown !== null &&
            typeof (vulnerabilityUnknown as Record<string, unknown>).severity === 'string'
            ? ((vulnerabilityUnknown as Record<string, unknown>).severity as string)
            : '';

    return cvesUnknown
        .map((cveUnknown) => (typeof cveUnknown === 'string' ? cveUnknown.trim() : ''))
        .filter((cve) => cve.length > 0)
        .map((cve) => ({ cve, severity }));
};

export const useVulnerabilityData = () => {
    const { data: fileData, isLoading: fileDataLoading } = useFileData();
    const hasVulnerability = fileData?.additional_info?.includes('vulnerability');
    const fileHash = resolveResultFileSha1(fileData);
    const { serviceId } = useQueryFilter();
    const { apikey } = useApikey({ serviceId });

    const vulnerabilityResponseKey: SWRKey =
        hasVulnerability && typeof fileHash === 'string' && fileHash.length > 0 && apikey
            ? { url: `/v4/hash/${fileHash}/vulnerability`, headers: { apikey } }
            : null;

    const {
        data: vulnerabilityResponse,
        isLoading: responseLoading,
        isValidating: responseValidating,
    } = useSWRImmutable<VulnerabilityResponseInterface>(vulnerabilityResponseKey, {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        revalidateIfStale: false,
    });

    const getVulnerabilityDataKey = useCallback(
        (index: number, prevPageData: VulnerabilityDataInterface | null) => {
            const pageLength = prevPageData?.data?.length ?? 0;
            const offset = index === 0 ? pageLength : index * pageLength;

            if (!(hasVulnerability && typeof fileHash === 'string' && fileHash.length > 0)) {
                return null;
            }

            if (index > 0 && pageLength < VULNERABILITY_LIMIT) {
                return null;
            }

            if (!apikey) return null;
            return {
                url: `/v4/vulnerability/${fileHash}?limit=${VULNERABILITY_LIMIT}&offset=${offset}`,
                headers: { apikey },
            };
        },
        [fileHash, hasVulnerability, apikey],
    );

    const {
        data: vulnerabilityDataList,
        setSize,
        isLoading: pagesLoading,
        isValidating: pagesValidating,
    } = useSWRInfinite<VulnerabilityDataInterface>(getVulnerabilityDataKey, {
        revalidateFirstPage: false,
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });

    const paginatedData = useMemo(
        () =>
            (vulnerabilityDataList ?? []).reduce((accumulator: CVEsInterface[], value) => {
                if (value?.data) {
                    return [...accumulator, ...value.data];
                }

                return accumulator;
            }, []),
        [vulnerabilityDataList],
    );

    const lastVulnerabilityDataPage = vulnerabilityDataList?.at(-1);
    const hasLoadMore = Number(lastVulnerabilityDataPage?.data?.length) === VULNERABILITY_LIMIT;

    const data = useMemo<CVEsInterface[]>(() => {
        if (paginatedData.length > 0) {
            return paginatedData;
        }

        return parseHashVulnerabilityResponse(vulnerabilityResponse).map(
            (row) => ({ cve: row.cve, severity: row.severity }) as CVEsInterface,
        );
    }, [paginatedData, vulnerabilityResponse]);

    const totalCveCount = vulnerabilityResponse?.cves?.length ?? data.length;

    const loadMore = useCallback(() => {
        void setSize((currentSize) => currentSize + 1);
    }, [setSize]);

    return {
        data,
        hasLoadMore,
        isLoading: fileDataLoading || responseLoading || (pagesLoading && data.length === 0),
        isValidating: responseValidating || pagesValidating,
        loadMore,
        setSize,
        totalCveCount,
        vulnerabilityResponse,
    };
};

export const useHashScanHistory = () => {
    const { data: fileData } = useFileData();
    const fileHash = resolveResultFileSha1(fileData);

    const { serviceId } = useQueryFilter();
    const { apikey } = useApikey({ serviceId });
    const key: SWRKey =
        typeof fileHash === 'string' && fileHash.length > 0 && apikey
            ? { url: `/v4/hash/${fileHash}/scanhistory`, headers: { apikey } }
            : null;

    return useSWRImmutable<unknown>(key, {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });
};

export const useFileComments = () => {
    const { user } = useUser();
    const { data: fileData } = useFileData();
    const fileHash = resolveResultFileSha1(fileData);
    const apikey = user?.portal_api_key;

    const key: SWRKey =
        typeof fileHash === 'string' && fileHash.length > 0 && apikey
            ? { url: `/v4/comment/${fileHash}`, headers: { apikey } }
            : null;

    return useSWR<unknown>(key, {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });
};

const postCommunityFileCommentFetcher: MutationFetcher<unknown, SWRKey, { comment: string; }> = async (
    { url, ...config },
    { arg },
) => axios.post(url, arg, config).then((response) => response.data);

export const usePostCommunityFileComment = () => {
    const { user } = useUser();
    const { data: fileData } = useFileData();
    const hash = resolveResultFileSha1(fileData);
    const apikey = user?.portal_api_key;
    const key: SWRKey =
        typeof hash === 'string' && hash.length > 0 && apikey
            ? { url: `/v4/comment/${hash}`, headers: { apikey } }
            : null;

    return useSWRMutation(key, postCommunityFileCommentFetcher, { throwOnError: false });
};

export type HashUserVote = 'up' | 'down' | null;

const handleHashUserVote: Fetcher<HashUserVote, SWRKey> = async ({ url, ...config }) => {
    const userVoteResponse = await axios.get(url, { ...config }).then((response) => response.data);
    const userVoteData = Object.values(userVoteResponse)[0];

    if (userVoteData === 'upvote') {
        return 'up';
    }

    if (userVoteData === 'downvote') {
        return 'down';
    }

    return null;
};

const hashVotesFetcher: MutationFetcher<unknown, SWRKey> = async (config) =>
    axios(config).then((response) => response.data);

export const useHashCommunityVotes = () => {
    const { user } = useUser();
    const { data: fileData, mutate: mutateFileData, isLoading: fileDataLoading } = useFileData();
    const hash = resolveResultFileSha1(fileData);
    const isLoggedIn = user?.isLoggedIn === true;
    // Analysts are not allowed to vote; the backend rejects their requests (MDCL-17081).
    const canVote = isLoggedIn && user?.org_role !== 'analyst';
    // Community votes are tied to the user's account, so they must use the client apikey
    // rather than the ICAP/MDSS integration apikey (MDCL-17146).
    const apikey = user?.portal_api_key;

    const userVoteKey: SWRKey =
        hash && isLoggedIn && apikey ? { url: `/v4/hash/${hash}/vote`, headers: { apikey } } : null;
    const {
        data: userVote,
        mutate: mutateUserVote,
        isValidating: userVoteValidating,
    } = useSWRImmutable<HashUserVote>(userVoteKey, handleHashUserVote, {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });

    const deleteVoteKey: SWRKey =
        hash && isLoggedIn && apikey ? { url: `/v4/hash/${hash}/vote`, method: 'DELETE', headers: { apikey } } : null;
    const { trigger: deleteVote, isMutating: deleteIsMutating } = useSWRMutation(deleteVoteKey, hashVotesFetcher);

    const upVoteKey: SWRKey =
        hash && isLoggedIn && apikey
            ? { url: `/v4/hash/${hash}/upvote`, method: 'PUT', data: {}, headers: { apikey } }
            : null;
    const { trigger: upVote, isMutating: upIsMutating } = useSWRMutation(upVoteKey, hashVotesFetcher);

    const downVoteKey: SWRKey =
        hash && isLoggedIn && apikey
            ? { url: `/v4/hash/${hash}/downvote`, method: 'PUT', data: {}, headers: { apikey } }
            : null;
    const { trigger: downVote, isMutating: downIsMutating } = useSWRMutation(downVoteKey, hashVotesFetcher);

    const isVoteDisabled =
        fileDataLoading ||
        userVoteValidating ||
        deleteIsMutating ||
        upIsMutating ||
        downIsMutating ||
        !canVote ||
        !apikey;

    const applyVote = useCallback(
        async (direction: 'up' | 'down') => {
            if (!hash || !canVote) {
                return;
            }

            if (userVote === direction) {
                await deleteVote();
            } else if (direction === 'up') {
                await upVote();
            } else {
                await downVote();
            }

            await Promise.all([mutateUserVote(), mutateFileData()]);
        },
        [canVote, deleteVote, downVote, hash, mutateFileData, mutateUserVote, upVote, userVote],
    );

    return {
        applyVote,
        downCount: fileData?.votes?.down ?? 0,
        isVoteDisabled,
        upCount: fileData?.votes?.up ?? 0,
        userVote: userVote ?? null,
    };
};
